// Orchestrator AI Setup — compose phase 01-06 thành 1 flow fail-soft.
// Gọi sau `maybeCreateWorkspaceRemote` trong `finalizeWorkspaceScaffold`.
// Try/catch toàn bộ: success → log + audit ok. Fail → log warn + audit failed, KHÔNG throw —
// workspace vẫn dùng được, user setup AI lại qua `avatar ai setup`.
import { appendAuditEntry } from "./audit-log-appender.js";
import {
  getQuotaErrorHint,
  readClaudeCodeAuthInfo,
  triggerClaudeCodeAuthLogin,
  verifyClaudeCodeQuota,
} from "./check-claude-code-subscription-and-quota.js";
import {
  detectClaudeCodeInstallation,
  invalidateClaudeCodeInstallationCache,
} from "./detect-claude-code-installation.js";
import { installClaudeCodeViaNpm } from "./install-claude-code-via-npm.js";
import {
  type AiProviderChoice,
  detectGlobalClaudeSettings,
  promptAiProviderChoice,
} from "./prompt-ai-provider-choice.js";
import { setupAnthropicApiKeyAndModel } from "./setup-anthropic-api-key-and-model.js";
import { setupLLMLiteApiKeyAndModel } from "./setup-llmlite-api-key-and-model.js";
import { log } from "./terminal-logger.js";
import { writeClaudeSettings } from "./write-claude-settings-json-per-project.js";

// Default model alias cho Subscription path — Claude Code expect string vd "opus".
// User có thể edit settings.json sau nếu muốn dùng model khác.
const SUBSCRIPTION_DEFAULT_MODEL = "opus";

// v1.20.0: warnAboutPlaintextSecret removed — keys giờ lưu vào .envrc qua direnv,
// không còn plaintext trong settings.json.

export interface AiSetupArgs {
  workspacePath: string;
  // CI / test mode: skip mọi interactive prompt — caller phải đảm bảo không vào path cần input.
  // Hiện tại không dùng vì AI setup interactive — reserve cho future enhancement.
  autoYes?: boolean;
}

export type AiSetupResult =
  | {
      ok: true;
      provider: AiProviderChoice;
      model?: string;
      // Số model key được phép dùng (chỉ có với llmlite/anthropic — query /v1/models).
      // Subscription/use-global: undefined (không query được). Dùng để quyết định
      // hỏi ai-orchestrator (cần >1 model + non-subscription).
      availableModels?: string[];
    }
  | { ok: false; reason: string; phase?: string };

// Main orchestrator. Fail-soft — không throw.
export async function runAiSetupPhase(args: AiSetupArgs): Promise<AiSetupResult> {
  try {
    log.info("Setup AI provider cho workspace...");

    // Phase 01: detect Claude Code. Phase 02: install nếu thiếu.
    let info = detectClaudeCodeInstallation();
    if (!info.installed) {
      log.info("Chưa có Claude Code — sẽ tự cài qua npm.");
      installClaudeCodeViaNpm();
      // v1.13.0: Invalidate cache sau install để re-probe thấy binary mới.
      invalidateClaudeCodeInstallationCache();
      info = detectClaudeCodeInstallation();
      if (!info.installed) {
        throw new Error("Cài Claude Code xong nhưng vẫn không detect được binary.");
      }
    } else {
      log.success(`Claude Code đã có${info.version ? ` v${info.version}` : ""}`);
    }

    // Phase 04: prompt provider choice (hybrid global detect).
    const globalInfo = detectGlobalClaudeSettings();
    const choice = await promptAiProviderChoice(globalInfo);

    // Branch theo choice → call phase 03 hoặc 05 → call phase 06 (write settings).
    switch (choice) {
      case "subscription": {
        // Phase 03: auth check + optional quota verify.
        //
        // v1.12.2: Đổi strategy verify quota từ MANDATORY → OPTIONAL.
        // Cũ: luôn chạy `claude --print ok` (30-60s) để verify token còn valid.
        //   Vấn đề: trên mạng VN, prompt "ok" mất 30s+ → timeout 60s vẫn fail
        //   thường xuyên dù subscription thực sự OK. UX cực tệ.
        // Mới: trust `claude auth status` JSON output. Nếu có
        //   `subscriptionType` (max/pro/team) → trust ngay. Skip 60s probe.
        //   Token revoked sẽ phát hiện lazy khi user run claude command thật,
        //   không block setup wizard.
        let authInfo = readClaudeCodeAuthInfo();
        if (authInfo.state !== "authenticated") {
          triggerClaudeCodeAuthLogin();
          authInfo = readClaudeCodeAuthInfo();
        }

        // Trust path: auth status JSON có subscriptionType → active plan, skip probe.
        if (authInfo.state === "authenticated" && authInfo.subscriptionType) {
          await writeClaudeSettings(args.workspacePath, {
            provider: "subscription",
            model: SUBSCRIPTION_DEFAULT_MODEL,
          });
          await appendAuditEntry(
            "ai_setup",
            `provider=subscription,result=ok,plan=${authInfo.subscriptionType},probe=skipped`,
          );
          log.success(
            `AI ready · Subscription (${authInfo.subscriptionType}) · model=${SUBSCRIPTION_DEFAULT_MODEL}`,
          );
          return { ok: true, provider: "subscription", model: SUBSCRIPTION_DEFAULT_MODEL };
        }

        // Fallback: auth OK nhưng không có subscriptionType field (older claude version,
        // hoặc edge case JSON shape khác) → fallback verify quota như cũ.
        log.dim("Auth status không trả subscriptionType — verify quota (30-60s)...");
        let quota = await verifyClaudeCodeQuota();
        if (!quota.ok && quota.reason === "auth-expired") {
          log.warn("Token Claude Code đã hết hạn. Tự động re-login...");
          triggerClaudeCodeAuthLogin();
          quota = await verifyClaudeCodeQuota();
        }

        // Soft-pass: timeout/unknown KHÔNG block init. Auth status đã pass — trust.
        // Setup vẫn ghi settings.json, user dùng được. Token revoked sẽ fail lazy
        // ở first real call, user re-login manual qua `claude auth login`.
        if (!quota.ok && (quota.reason === "timeout" || quota.reason === "unknown")) {
          log.warn(`Probe verify ${quota.reason} — accept trust auth status. Tiếp tục.`);
          if (quota.detail?.trim()) log.warn(`  Chi tiết: ${quota.detail.slice(0, 200)}`);
          await writeClaudeSettings(args.workspacePath, {
            provider: "subscription",
            model: SUBSCRIPTION_DEFAULT_MODEL,
          });
          await appendAuditEntry(
            "ai_setup",
            `provider=subscription,result=ok,probe=${quota.reason}-soft-pass`,
          );
          log.success(
            `AI ready · Subscription (probe ${quota.reason}, soft-pass) · model=${SUBSCRIPTION_DEFAULT_MODEL}`,
          );
          return { ok: true, provider: "subscription", model: SUBSCRIPTION_DEFAULT_MODEL };
        }

        // Hard fail: quota actually exhausted / invalid key / rate limit — block và suggest LLMLite.
        if (!quota.ok) {
          const reason = quota.reason ?? "unknown";
          await appendAuditEntry(
            "ai_setup",
            `provider=subscription,result=no-quota,reason=${reason}`,
          );
          log.warn(`Subscription verify thất bại (${reason}).`);
          if (quota.detail?.trim()) log.warn(`  Chi tiết: ${quota.detail.slice(0, 200)}`);
          log.warn(`  → ${getQuotaErrorHint(reason)}`);
          return { ok: false, reason: `subscription-${reason}`, phase: "quota" };
        }

        await writeClaudeSettings(args.workspacePath, {
          provider: "subscription",
          model: SUBSCRIPTION_DEFAULT_MODEL,
        });
        await appendAuditEntry("ai_setup", "provider=subscription,result=ok");
        log.success(`AI ready · Subscription · model=${SUBSCRIPTION_DEFAULT_MODEL}`);
        return { ok: true, provider: "subscription", model: SUBSCRIPTION_DEFAULT_MODEL };
      }

      case "llmlite": {
        // Phase 05: API key + model picker + verify.
        const llmConfig = await setupLLMLiteApiKeyAndModel();
        // v3: bỏ direnv — key ghi thẳng vào settings.json (chmod 600 + .claude/.gitignore
        // bảo vệ). Claude Code đọc trực tiếp env field. skipApiKey=false.
        await writeClaudeSettings(args.workspacePath, {
          provider: "llmlite",
          apiKey: llmConfig.apiKey,
          baseUrl: llmConfig.baseUrl,
          model: llmConfig.model,
          skipApiKey: false,
        });
        await appendAuditEntry(
          "ai_setup",
          `provider=llmlite,result=ok,model=${llmConfig.model},base=${llmConfig.baseUrl},storage=settings.json`,
        );
        log.success(`AI ready · LLMLite (NAL) · model=${llmConfig.model} · ${llmConfig.baseUrl}`);
        return {
          ok: true,
          provider: "llmlite",
          model: llmConfig.model,
          availableModels: llmConfig.availableModels,
        };
      }

      case "anthropic": {
        // v1.6.0: Anthropic API direct (key sk-ant-..., x-api-key auth).
        // v3: key ghi vào settings.json (chmod 600 + gitignored).
        const anthropicConfig = await setupAnthropicApiKeyAndModel();
        await writeClaudeSettings(args.workspacePath, {
          provider: "anthropic",
          apiKey: anthropicConfig.apiKey,
          baseUrl: anthropicConfig.baseUrl,
          model: anthropicConfig.model,
          skipApiKey: false,
        });
        await appendAuditEntry(
          "ai_setup",
          `provider=anthropic,result=ok,model=${anthropicConfig.model},storage=settings.json`,
        );
        log.success(
          `AI ready · Anthropic Direct · model=${anthropicConfig.model} · ${anthropicConfig.baseUrl}`,
        );
        return {
          ok: true,
          provider: "anthropic",
          model: anthropicConfig.model,
          availableModels: anthropicConfig.availableModels,
        };
      }

      case "use-global": {
        if (!globalInfo.rawSettings) {
          throw new Error("use-global chọn nhưng không đọc được global settings.");
        }
        await writeClaudeSettings(args.workspacePath, {
          provider: "use-global",
          sourceSettings: globalInfo.rawSettings,
        });
        await appendAuditEntry("ai_setup", "provider=use-global,result=ok");
        log.success(`AI ready · Copy từ global config (${globalInfo.baseUrl ?? "subscription"})`);
        return { ok: true, provider: "use-global", model: globalInfo.model };
      }
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log.warn(`AI setup thất bại: ${message}`);
    log.dim("Workspace vẫn sẵn sàng. Setup AI sau qua: avatar ai setup");
    await appendAuditEntry("ai_setup", `result=failed,error=${message.slice(0, 200)}`);
    return { ok: false, reason: message };
  }
}
